import { z } from 'zod';
import { ARCHETYPE_IDS, CHARACTER_NAME_MAX_LENGTH, ELEMENT_IDS, MAX_POTENTIAL_POINTS, MIN_POTENTIAL_POINTS, SPECIAL_MOVE_NAME_MAX_LENGTH, TRAIT_IDS, } from '../constants/index.js';
import { UNPRINTABLE } from './text.js';
/**
 * zod の enum は定数配列から作る。表示名や ID を増やしたときにスキーマ側の更新を
 * 忘れる、という形のドリフトが起きなくなる。
 */
export const archetypeIdSchema = z.enum(ARCHETYPE_IDS);
export const elementIdSchema = z.enum(ELEMENT_IDS);
export const traitIdSchema = z.enum(TRAIT_IDS);
const statSchema = z.number().int().min(0);
/**
 * 必殺技の名前の入力（#204、ADR-028）。**前後の空白を削ってから** 1〜`SPECIAL_MOVE_NAME_MAX_LENGTH`（コードポイント）。
 * 見せられない文字（改行・向きの制御文字など）は弾く（ニックネームと同じ規則。`UNPRINTABLE`）。
 *
 * 演出だけに使い、エンジンは読まない（企画書 §40-1）。中身（悪い言葉・個人が分かる名前）は、サーバーが AI で確かめる（#258）。
 */
export const specialMoveNameSchema = z
    .string()
    .trim()
    .min(1, 'ひっさつわざの なまえを 入れてください')
    .max(SPECIAL_MOVE_NAME_MAX_LENGTH, `ひっさつわざの なまえは ${SPECIAL_MOVE_NAME_MAX_LENGTH} 文字までにしてください`)
    .refine((value) => !UNPRINTABLE.test(value), {
    message: 'ひっさつわざの なまえに 使えない文字が入っています',
});
/**
 * 選手の名前の入力（#271）。**前後の空白を削ってから** 1〜`CHARACTER_NAME_MAX_LENGTH`（コードポイント）。
 * 見せられない文字（改行・向きの制御文字など）は弾く（必殺技の名前・ニックネームと同じ規則。`UNPRINTABLE`）。
 * 中身（悪い言葉・個人が分かる名前）は、サーバーが AI で確かめる（#258）。
 *
 * 応答の `characterSchema` の `name` には使わない（すでに保存されている名前を、読むときに弾かない）。
 */
export const characterNameSchema = z
    .string()
    .trim()
    .min(1, 'なまえを 入れてください')
    .max(CHARACTER_NAME_MAX_LENGTH, `なまえは ${CHARACTER_NAME_MAX_LENGTH} 文字までにしてください`)
    .refine((value) => !UNPRINTABLE.test(value), {
    message: 'なまえに 使えない文字が入っています',
});
export const characterStatsSchema = z.object({
    hp: statSchema,
    atk: statSchema,
    def: statSchema,
    spd: statSchema,
});
export const potentialPointsSchema = z
    .number()
    .int()
    .min(MIN_POTENTIAL_POINTS)
    .max(MAX_POTENTIAL_POINTS);
/**
 * ステ振りの検証（企画書 §8.2）。
 *
 * このスキーマが `@drawgon/shared` にある理由は ADR-005 に書いてある。要約すると、
 * **この規則はクライアントとサーバーの両方で必ず走る**ため。クライアントでは入力中に
 * 残りポイントを出すため、サーバーでは不正を防ぐため（§20「クライアントを信用しない」）。
 * 同じ規則を 2 箇所に書けば、いつか必ずズレる。
 *
 * **極振りは弾かない**（企画書 §32）。HP 5 / ATK 40 / DEF 5 / SPD 40 は合法で、
 * 「一撃で倒せる／一撃で倒される」という明確なメリットとデメリットを持つビルドとして
 * 成立させる。検証するのは合計が一致するかどうかだけ。
 */
export const statAllocationSchema = z
    .object({
    stats: characterStatsSchema,
    potentialPoints: potentialPointsSchema,
})
    .superRefine((value, ctx) => {
    checkTotal(value.stats, value.potentialPoints, ctx);
});
const STAT_LABELS = { hp: 'HP', atk: 'ATK', def: 'DEF', spd: 'SPD' };
/**
 * 鑑定の基礎の能力値を下回らないステ振り（ADR-019）。
 *
 * プレイヤーが足せるのは鑑定ボーナスだけで、**基礎の能力値より下げられない。** 絵の特徴を数値に
 * 残すため。合計は `statAllocationSchema` と同じく素質ポイントに一致させる（ボーナスを使い切る）。
 */
export const appraisalAllocationSchema = z
    .object({
    stats: characterStatsSchema,
    baseStats: characterStatsSchema,
    potentialPoints: potentialPointsSchema,
})
    .superRefine((value, ctx) => {
    for (const key of Object.keys(STAT_LABELS)) {
        if (value.stats[key] < value.baseStats[key]) {
            ctx.addIssue({
                code: 'custom',
                path: ['stats', key],
                message: `${STAT_LABELS[key]} は鑑定で決まった ${value.baseStats[key]} より下げられません`,
            });
        }
    }
    checkTotal(value.stats, value.potentialPoints, ctx);
});
/** 合計が素質ポイントに一致するか。余りと超過で文言を分ける。 */
function checkTotal(stats, potentialPoints, ctx) {
    const total = stats.hp + stats.atk + stats.def + stats.spd;
    if (total === potentialPoints)
        return;
    const diff = potentialPoints - total;
    ctx.addIssue({
        code: 'custom',
        path: ['stats'],
        message: diff > 0
            ? `ポイントが ${diff} 余っています（配分 ${total} / 素質 ${potentialPoints}）`
            : `ポイントを ${-diff} 超過しています（配分 ${total} / 素質 ${potentialPoints}）`,
    });
}
/**
 * 配分の残りポイント。正なら余り、負なら超過。
 *
 * 入力中の UI 表示に使う。スキーマの検証と同じ計算をクライアントで再実装させないために
 * ここに置く。
 */
export function remainingPoints(stats, potentialPoints) {
    return potentialPoints - (stats.hp + stats.atk + stats.def + stats.spd);
}
/**
 * キャラクター（`GET /api/v1/characters` などのレスポンス）。型は `Character`。
 *
 * **素質ポイントなどの値の出どころは AI 鑑定**（`createCharacterSchema` のコメント）。
 */
export const characterSchema = z.object({
    id: z.string().min(1),
    ownerId: z.string().min(1),
    name: z.string().min(1).max(CHARACTER_NAME_MAX_LENGTH),
    /**
     * 描いた絵。**CPU は持たない**（ADR-020。`originalImageUrl` が空で保存されている）。
     *
     * 任意にしてあるのは、大会の詳細（`GET /tournaments/:id`）が**CPU を含む参加者を返す**ため。
     * 必須のままだと、CPU が混ざった時点でレスポンス全体がスキーマに違反する（#149 の契約テストが
     * 捕まえた）。プレイヤーの選手は作成のときに必ず絵を持つので、ここを任意にしても入力の検証は
     * 弱くならない（`createCharacterSchema` は URL を受け取らず、鑑定から導く）。
     */
    originalImageUrl: z.string().url().optional(),
    spriteImageUrl: z.string().url().optional(),
    /** AI 鑑定とサーバー側ルールで確定した総合素質ポイント（50〜100）。 */
    potentialPoints: potentialPointsSchema,
    archetypeId: archetypeIdSchema,
    /** 相性でダメージが変わる（ADR-027）。 */
    elementId: elementIdSchema.optional(),
    traitIds: z.array(traitIdSchema),
    stats: characterStatsSchema,
    /**
     * 鑑定で決まった基礎の能力値（ADR-019）。**振り直し（`PUT /characters/:id/stats`）はこれより下げられない。**
     *
     * 無いキャラクターは振り直せない（鑑定の導入 #97 より前か、ADR-019 より前の版の鑑定から作った）。
     * 鑑定ボーナスの量は送らない。`potentialPoints − baseStats の合計` で決まり、同じ値を 2 か所に持たないため。
     */
    baseStats: characterStatsSchema.optional(),
    /** 勝敗数（キャラクター同士の対戦・大会）。今のランキングの順位に使う。**CPU 戦は数えない。** */
    wins: z.number().int().min(0),
    losses: z.number().int().min(0),
    draws: z.number().int().min(0),
    /** 練習の戦績（CPU 戦、ADR-020）。ランキングには使わない。 */
    cpuRecord: z.object({
        wins: z.number().int().min(0),
        losses: z.number().int().min(0),
        draws: z.number().int().min(0),
    }),
    createdAt: z.string(),
    /**
     * 必殺技の名前（#204、ADR-028）。**名前の無い選手は省く**（`null` にしない）。演出だけに使う。
     * キャラ名と同じく、ランキング・大会の詳細・ともだちと対戦の相手にも載る。
     */
    specialMoveName: z.string().min(1).max(SPECIAL_MOVE_NAME_MAX_LENGTH).optional(),
});
/**
 * 自分の選手（`/characters` の応答）。型は `OwnCharacter`。
 *
 * **公開の応答（大会の詳細・ランキング・ともだちと対戦の相手）は `characterSchema` のまま。** ここにしか無い項目
 * （お気に入り）を他人に見せない。お気に入りにした日時は、その人がいつアプリを使っていたかでもある。
 */
export const ownCharacterSchema = characterSchema.extend({
    /** お気に入りにした日時（ISO 8601、#208。コードの名前は pin）。無ければお気に入りでない。 */
    pinnedAt: z.string().optional(),
    /**
     * 出ている進行中（`ENTRY` / `RUNNING`）の大会の ID（#229）。ADR-014 の「大会中」。無ければ大会に出ていない。
     *
     * **保存せず、読むたびに進行中の大会から導出する。** 大会中はステ振りを振り直せない（409）。
     */
    activeTournamentId: z.string().min(1).optional(),
});
/**
 * キャラクター作成の入力（`POST /api/v1/characters`）。
 *
 * **素質ポイント・系統・属性・特性・画像は受け取らない。** サーバーが AI 鑑定
 * （`POST /api/v1/characters/analyze`）の結果から引く。受け取ると、値を書き換えて送るだけで
 * 任意の強さのキャラクターが作れる（企画書 §40-7「クライアントを信用しない」）。
 *
 * zod はスキーマに無いキーを落とすので、`potentialPoints` などを混ぜて送っても使われない。
 */
export const createCharacterSchema = z.object({
    appraisalId: z.string().min(1),
    name: characterNameSchema,
    stats: characterStatsSchema,
    /** 必殺技の名前（#204）。**任意**。AI の案はアプリが入力欄に入れ、プレイヤーが直す（サーバーは案を勝手に使わない）。 */
    specialMoveName: specialMoveNameSchema.optional(),
});
/**
 * ステ振りの振り直しの入力（`PUT /api/v1/characters/:characterId/stats`、#110）。
 *
 * **基礎の能力値と素質ポイントは受け取らない。** サーバーがキャラクターの行から引き、
 * `appraisalAllocationSchema` で検証する（企画書 §40-7）。
 */
export const updateCharacterStatsSchema = z.object({
    stats: characterStatsSchema,
});
/**
 * 必殺技の名前を変える入力（`PUT /api/v1/characters/:characterId/special-move`、#204）。
 * 作ったあとに付ける・変える。消す（空にする）ことはできない。
 */
export const updateSpecialMoveSchema = z.object({
    specialMoveName: specialMoveNameSchema,
});
/**
 * 選手の名前を変える入力（`PUT /api/v1/characters/:characterId/name`、#270）。規則は作るときと同じ（`characterNameSchema`）。
 */
export const updateCharacterNameSchema = z.object({
    name: characterNameSchema,
});
